import { AxiosRequestHeaders } from "axios";

import { getInstance } from "../../core/axios-gateway";
import { TRestListData, TRestListParams } from "../../types/rest";
import { TProduct, TSearchByText } from "../../types/product";
import { SEERI_SUPPLIER_ID } from "@/constants/env";

const BASE_PATH = "/api/products/v2";

export const getProductsV2 = async (
  params: TRestListParams,
  headers?: AxiosRequestHeaders
) => {
  return getInstance()
    .get<TRestListData<TProduct>>(BASE_PATH, { params, headers })
    .then((res) => res.data);
};

export const getProductsByIdsV2 = async (ids: string[]) => {
  const opts = {
    headers: { "x-log": "omit" },
  };
  // Migrated to products-go POST /api/products/v5/by-ids. Body and response
  // are identical to the legacy /by-ids/v2; only the URL changed.
  return getInstance()
    .post<TProduct[]>("/api/products/v5/by-ids", { ids }, opts)
    .then((res) => res.data);
};

// getProductV2 hits products-go GET /api/products/v5/:id. Replaces the legacy
// supplier-agnostic Java GET /api/products/v2/{id}: products-go's by-id lookup
// has its supplier_id filter disabled (see FindByIDAndSupplier TODO), so the id
// alone resolves the product regardless of owning supplier — same as before.
// x-supplier-id is still required by the endpoint; fall back to SEERI_SUPPLIER_ID
// when none is supplied, mirroring create/updateProductV2.
export const getProductV2 = async (id: string, params?: any) => {
  const supplierId = params?.supplierId || SEERI_SUPPLIER_ID;
  return getInstance()
    .get<TProduct>(`/api/products/v5/${id}`, {
      params,
      headers: { "x-supplier-id": supplierId },
    })
    .then((res) => res.data);
};

// createProductV2 hits products-go POST /api/products/v5 (same endpoint
// suppliers-web uses for product creation). x-supplier-id is required by
// the v5 endpoint — backoffice falls back to SEERI_SUPPLIER_ID when the
// payload doesn't carry one.
export const createProductV2 = async (data: TProduct) => {
  const supplierId = (data as any)?.supplierId || SEERI_SUPPLIER_ID;
  return getInstance()
    .post<TProduct>("/api/products/v5", data, {
      headers: { "x-supplier-id": supplierId },
    })
    .then((res) => res.data);
};

// updateProductV2 hits products-go PUT /api/products/v5/:id. Same x-supplier-id
// requirement; same fallback. Partial-update semantics: only the fields you
// pass are touched.
export const updateProductV2 = async (id: string, data: Partial<TProduct>) => {
  const supplierId = (data as any)?.supplierId || SEERI_SUPPLIER_ID;
  return getInstance()
    .put<TProduct>(`/api/products/v5/${id}`, data, {
      headers: { "x-supplier-id": supplierId },
    })
    .then((res) => res.data);
};

export const deleteProductV2 = async (id: string) => {
  return getInstance()
    .delete(`${BASE_PATH}/${id}`)
    .then((res) => res.data);
};

export const updateProductV2ProductGroup = async (
  id: string,
  productGroupId: string
) => {
  return getInstance()
    .put<TProduct>(`${BASE_PATH}/parent-change`, {
      productId: id,
      groupProductId: productGroupId,
    })
    .then((res) => res.data);
};

export const searchProducts = async (body: {
  data: TSearchByText;
  params?: TRestListParams;
  headers?: AxiosRequestHeaders;
}) => {
  return getInstance()
    .post<TRestListData<TProduct>>(`${BASE_PATH}/search`, body.data, {
      params: body.params,
      headers: body.headers,
    })
    .then((res) => res.data);
};
